import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {useTranslation} from 'react-i18next';

export const MessageContainer = props => (
  <Box
    margin="auto"
    textAlign="center"
    flexShrink={0}
    px={4}
    color="text.secondary"
    {...props}
  />
);

export const EmptyMessageContainer = ({icon, children, ...props}) => (
  <MessageContainer {...props}>
    {icon && <Box fontSize={48}>{icon}</Box>}
    {children}
  </MessageContainer>
);

EmptyMessageContainer.propTypes = {
  icon: PropTypes.element,
  children: PropTypes.node,
};

export const LoadingMessage = props => {
  const {t} = useTranslation();

  return (
    <MessageContainer data-qe="loading-message">
      <Box marginBottom={2} width="100%">
        <CircularProgress color="primary" size={props.progressSize} />
      </Box>
      <Typography
        variant="h5"
        color="inherit"
        gutterBottom
        {...props.titleTypographyProps}
      >
        {props.message
          ? props.message
          : t('messageContainer.titleLoading', {item: props.suffix})}
      </Typography>
      <Typography
        variant="h6"
        color="inherit"
        {...props.subheaderTypographyProps}
      >
        {t('messageContainer.messageLoading')}
      </Typography>
    </MessageContainer>
  );
};

LoadingMessage.propTypes = {
  message: PropTypes.string,
  suffix: PropTypes.string,
  titleTypographyProps: PropTypes.object,
  subheaderTypographyProps: PropTypes.object,
  progressSize: PropTypes.number,
};

LoadingMessage.defaultProps = {
  suffix: '',
};

export const ErrorMessage = ({error, message, icon, children, ...props}) => {
  const {t} = useTranslation();

  return (
    <EmptyMessageContainer
      icon={
        icon && <ErrorOutlineOutlinedIcon fontSize="inherit" color="error" />
      }
      data-qe="error-message"
    >
      {children ? (
        children
      ) : (
        <Fragment>
          <Typography variant="h6" color="inherit" gutterBottom>
            {t('error.title')}
          </Typography>
          {message && (
            <Typography variant="h6" color="inherit" gutterBottom>
              {message}
            </Typography>
          )}
          {error && (
            <Typography variant="overline" color="inherit" gutterBottom>
              {error}
            </Typography>
          )}
          <Box mt={4}>
            <Typography variant="h6" color="inherit" gutterBottom>
              {t('messageContainer.promptReload', {
                componentName: props.componentName,
              })}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            marginTop={2}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={() => props.onRetry()}
              startIcon={<RefreshIcon />}
            >
              {t('messageContainer.buttonReload')}
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              href="https://support.planet.com/hc/en-us/requests/new"
              target="_blank"
              rel="noreferrer noopener"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              {t('messageContainer.buttonReport')}
            </Button>
          </Stack>
        </Fragment>
      )}
    </EmptyMessageContainer>
  );
};

ErrorMessage.propTypes = {
  error: PropTypes.string,
  message: PropTypes.string,
  children: PropTypes.node,
  icon: PropTypes.bool,
  componentName: PropTypes.string,
  onRetry: PropTypes.func,
};

ErrorMessage.defaultProps = {
  icon: true,
  componentName: 'page',
};

export default MessageContainer;
